import type {
  Chart,
  ChartCallbackFunction,
  HTMLDOMElement,
  Options,
  Point,
  Pointer,
  Series,
  StockChart,
  SVGRenderer as SVGRendererType,
} from "highcharts/highstock"
import { useEffect, useState } from "react"

type ChartCallback = (
  renderTo: string | HTMLDOMElement,
  options: Options,
  callback?: ChartCallbackFunction
) => Chart
type StockChartCallback = (
  renderTo: string | HTMLDOMElement,
  options: Options,
  callback?: ChartCallbackFunction
) => StockChart

type Charts = {
  stockChart: StockChartCallback
  chart: ChartCallback
  SVGRenderer: SVGRendererType
}

/** Runtime fields absent from the published types. */
type StatefulSeries = Series & { state?: string }
type ChartPointer = Pointer & { chart: Chart }

let highcharts: Promise<Charts>

// Dynamically fetch highcharts as it's used in a useEffect and not need to be part of initial bundle
function getHighcharts(): Promise<Charts> {
  if (!highcharts) {
    highcharts = import(/* webpackPreload: true */ "highcharts/highstock").then(
      (Highcharts) => {
        // Highcharts only ever sets series inactive on hover, never back, until the
        // hovered series changes. With our inactive opacity of 0, a series that has
        // no point on the left stays hidden when the cursor moves to where it has
        // data. Reset them first, so only series with no point at this date are inactive.
        Highcharts.wrap(
          Highcharts.Pointer.prototype,
          "applyInactiveState",
          function (
            this: ChartPointer,
            proceed: (points?: Point[]) => void,
            points?: Point[]
          ) {
            for (const serie of this.chart.series as StatefulSeries[]) {
              if (serie.state === "inactive") serie.setState("")
            }
            proceed.call(this, points)
          }
        )

        return {
          stockChart: Highcharts.stockChart,
          chart: Highcharts.chart,
          SVGRenderer: Highcharts.SVGRenderer,
        } as unknown as Charts
      }
    )
  }

  return highcharts
}

// Type-only namespace re-export so app code can reference Highcharts types
// without importing the highcharts package (restricted by lint in frontend).
export type * as Highcharts from "highcharts"
export type { Options }

export const useHighcharts = () => {
  const [highchartsInstance, setHighchartInstance] = useState<Charts>()

  useEffect(() => {
    getHighcharts().then((highchart) => setHighchartInstance(highchart))
  }, [])

  return highchartsInstance
}
